import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, CalendarClock, CheckCircle2, Clock, XCircle, CalendarCheck } from "lucide-react";
import { api } from "@/lib/api";
import { StatCard, StatusBadge, fmtDate } from "@/pages/admin/adminUi";

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  if (!stats) return <div className="flex h-64 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-terracotta border-t-transparent" /></div>;

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-serif text-3xl text-charcoal">Dashboard</h1>
      <p className="mt-1 text-sm text-taupe">Overview of enquiries and appointment requests.</p>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard label="Total Enquiries" value={stats.totalEnquiries} tone="terracotta" icon={MessageSquare} />
        <StatCard label="New" value={stats.new} icon={Clock} />
        <StatCard label="Contacted" value={stats.contacted} icon={MessageSquare} />
        <StatCard label="Scheduled" value={stats.scheduled} icon={CalendarCheck} />
        <StatCard label="Completed" value={stats.completed} icon={CheckCircle2} />
        <StatCard label="Cancelled" value={stats.cancelled} icon={XCircle} />
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <StatCard label="Total Appointments" value={stats.totalAppointments} icon={CalendarClock} />
        <StatCard label="New Appointment Requests" value={stats.newAppointments} icon={CalendarClock} />
      </div>

      <div className="mt-10 grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-soft-border bg-cream p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-xl text-charcoal">Recent Enquiries</h2>
            <Link to="/admin/enquiries" className="text-sm font-semibold text-terracotta">View all</Link>
          </div>
          <div className="mt-4 divide-y divide-soft-border">
            {stats.recentEnquiries.length === 0 && <p className="py-6 text-sm text-taupe">No enquiries yet.</p>}
            {stats.recentEnquiries.map((e) => (
              <div key={e.id} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="truncate font-medium text-charcoal">{e.name}</p>
                  <p className="truncate text-xs text-taupe">{e.service || "General"} · {fmtDate(e.createdAt)}</p>
                </div>
                <StatusBadge status={e.status} />
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-soft-border bg-cream p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-xl text-charcoal">Recent Appointment Requests</h2>
            <Link to="/admin/appointments" className="text-sm font-semibold text-terracotta">View all</Link>
          </div>
          <div className="mt-4 divide-y divide-soft-border">
            {stats.recentAppointments.length === 0 && <p className="py-6 text-sm text-taupe">No appointment requests yet.</p>}
            {stats.recentAppointments.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="truncate font-medium text-charcoal">{a.patientName}</p>
                  <p className="truncate text-xs text-taupe">{a.consultationType || "Consultation"} · {fmtDate(a.createdAt)}</p>
                </div>
                <StatusBadge status={a.status} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
